import { buildCombatActionCatalog } from './combat/CombatActionCatalog';

const ACTION_SECTION_DEFINITIONS = Object.freeze([
  { key: 'actions', title: 'Actions' },
  { key: 'bonusActions', title: 'Bonus Actions' },
  { key: 'reactions', title: 'Reactions' },
  { key: 'specialActions', title: 'Special Actions' }
]);

const normalizeDisplayText = (value) => String(value || '')
  .trim()
  .replace(/\s+/g, ' ');

const normalizeNamedAction = (entry) => {
  const name = normalizeDisplayText(entry?.name || entry?.title || entry?.label || '');
  const correctedName = name.replace(/mage hand (?:ledgerdemain|legerdemaine)/gi, 'Mage Hand Legerdemain');

  if (/^telekinetic$/i.test(name)) {
    const description = normalizeDisplayText(entry?.desc || entry?.description || entry?.text || '');
    const shoveSentence = description.match(/(?:as a bonus action[^.]*\.(?:[^.]*saving throw[^.]*\.)?(?:[^.]*pushed[^.]*\.)?)/i)?.[0];
    return {
      ...entry,
      name: 'Telekinetic Shove',
      description: shoveSentence || 'As a Bonus Action, choose a creature you can see within 30 feet. It makes a Strength saving throw or is moved 5 feet toward or away from you.',
      activationType: 'bonus action',
      actionType: 'bonus action',
      type: 'bonus'
    };
  }

  return {
    ...entry,
    name: correctedName,
    description: normalizeDisplayText(entry?.desc || entry?.description || entry?.text || '')
      .replace(/mage hand (?:ledgerdemain|legerdemaine)/gi, 'Mage Hand Legerdemain')
  };
};

const hasRecordedFeature = (subject, featureName) => {
  const target = String(featureName || '').toLowerCase();
  return [subject, subject?.data, subject?.schemaData, subject?.canonicalData, subject?.characterData]
    .filter(Boolean)
    .some((branch) => ['feats', 'features', 'classFeatures', 'traits'].some((field) => (
      Array.isArray(branch?.[field]) && branch[field].some((entry) => (
        normalizeDisplayText(typeof entry === 'string' ? entry : entry?.name || entry?.title).toLowerCase() === target
      ))
    )));
};

export const formatMovementSummary = (speed) => {
  if (speed === null || speed === undefined || speed === '') return '';
  if (typeof speed === 'number' && Number.isFinite(speed)) return `${speed} ft.`;
  if (typeof speed === 'string') return speed;
  if (typeof speed !== 'object' || Array.isArray(speed)) return '';

  const speedEntries = Object.entries(speed)
    .map(([mode, value]) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) return '';
      return `${mode} ${numericValue} ft.`;
    })
    .filter(Boolean);

  return speedEntries.join(', ');
};

export const formatCombatRangeSummary = (range) => {
  if (!range) return '';
  if (typeof range === 'number' && Number.isFinite(range)) return `${range} ft.`;
  if (typeof range === 'string') return range
    .replace(/\b(\d+)\s*(?:feet|foot|ft)(?:\.)?/gi, '$1 ft.')
    .replace(/\.\./g, '.')
    .replace(/\bself\b/gi, 'Self')
    .replace(/\btouch\b/gi, 'Touch');
  if (typeof range !== 'object' || Array.isArray(range)) return '';

  const normalRange = Number(range.normal);
  const longRange = Number(range.long);
  const reach = Number(range.reach);

  if (Number.isFinite(reach) && reach > 0) {
    return `${reach} ft. reach`;
  }

  if (Number.isFinite(normalRange) && Number.isFinite(longRange) && longRange > normalRange) {
    return `${normalRange}/${longRange} ft.`;
  }

  if (Number.isFinite(normalRange) && normalRange > 0) {
    return `${normalRange} ft.`;
  }

  return '';
};

export const formatCombatEntryTypeLabel = (type) => {
  const normalizedType = normalizeDisplayText(type).toLowerCase();
  return ({
    spell: 'Spell',
    weapon: 'Weapon',
    attack: 'Attack',
    action: 'Action',
    bonus: 'Bonus Action',
    reaction: 'Reaction',
    free: 'Free Action',
    object: 'Object Interaction',
    legendary: 'Legendary Action',
    move: 'Movement'
  })[normalizedType] || '';
};

const formatDamageEntry = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  if (typeof entry !== 'object' || Array.isArray(entry)) return '';

  const dice = normalizeDisplayText(entry.dice || entry.formula || entry.roll || entry.amount || '');
  const modifier = Number(entry.modifier);
  const modifierLabel = Number.isFinite(modifier) && modifier !== 0
    ? `${modifier > 0 ? '+' : ''}${modifier}`
    : '';
  const damageType = normalizeDisplayText(entry.type || entry.damageType || '');
  const baseLabel = `${dice}${modifierLabel}`.trim();

  if (baseLabel && damageType) {
    return `${baseLabel} ${damageType}`;
  }

  return baseLabel || damageType;
};

export const formatCombatDamageSummary = (damage) => {
  if (!damage) return '';
  if (typeof damage === 'string') return damage;
  if (!Array.isArray(damage)) return formatDamageEntry(damage);

  return damage
    .map((entry) => formatDamageEntry(entry))
    .filter(Boolean)
    .join(', ');
};

const getSectionKeyForEntry = (entry) => {
  const activationType = normalizeDisplayText(entry?.activationType || entry?.actionType || 'action').toLowerCase();
  const entryType = normalizeDisplayText(entry?.type).toLowerCase();

  if (activationType.includes('bonus') || entryType === 'bonus') return 'bonusActions';
  if (activationType.includes('reaction') || entryType === 'reaction') return 'reactions';
  if (
    activationType.includes('free')
    || activationType.includes('object')
    || activationType.includes('movement')
    || entryType === 'free'
    || entryType === 'object'
    || entryType === 'move'
    || entryType === 'legendary'
  ) {
    return 'specialActions';
  }
  return 'actions';
};

/**
 * Detect if entry is a spell based on spell-specific properties
 * date_of_change: 2026-05-04
 * rule_reference: Spells have level (0-9), school, saving throw DC, or spell attack; attacks have weapon/attackBonus
 */
const isSpellEntry = (entry) => {
  if (!entry) return false;
  const name = normalizeDisplayText(entry.name || '').toLowerCase();
  const desc = String(entry.description || entry.desc || '').toLowerCase();
  
  // Explicit spell type
  if (normalizeDisplayText(entry.type).toLowerCase() === 'spell') return true;
  // Has spell level (0 = cantrip)
  if (typeof entry.level === 'number' && entry.level >= 0 && entry.level <= 9) return true;
  // Has spell school
  if (entry.school) return true;
  // Has saving throw structured property but no attack bonus (save-or-suck spell)
  if (entry.savingThrow && !entry.attackBonus) return true;
  
  // Description has spell save DC (e.g., "DC 14 Strength saving throw")
  // This catches Entangle, Faerie Fire, Thunderwave, etc.
  if (/dc\s*\d+\s*(strength|dexterity|constitution|intelligence|wisdom|charisma)\s*saving\s*throw/i.test(desc)) {
    // Make sure it's not a weapon attack with a rider saving throw
    if (!/melee\s*weapon|ranged\s*weapon/i.test(desc)) {
      return true;
    }
  }
  
  // Ranged spell attack or melee spell attack
  if (/(ranged|melee)\s*spell\s*attack/i.test(desc)) return true;
  
  // Description mentions spellcasting keywords but not weapon attack patterns
  if (/(spell\s*slot|casting\s*time|verbal|somatic|material|concentration|duration)/i.test(desc)) {
    // But exclude if it clearly describes a weapon attack
    if (!/(melee|ranged|weapon|attack\s+roll|to\s*hit)/i.test(desc)) {
      return true;
    }
  }
  
  // Name contains spell indicators (cantrip, spell slot references)
  if (/\(cantrip\)|\(\d+(st|nd|rd|th)\s*level\)/i.test(name)) return true;
  
  return false;
};

/**
 * Detect if entry is an attack based on weapon/attack properties
 * date_of_change: 2026-05-04
 * rule_reference: Attacks have weapon type, attack bonus, or weapon attack pattern
 */
const isAttackEntry = (entry) => {
  if (!entry) return false;
  const name = normalizeDisplayText(entry.name || '').toLowerCase();
  const type = normalizeDisplayText(entry.type).toLowerCase();
  const desc = String(entry.description || entry.desc || '').toLowerCase();
  
  if (type === 'weapon' || type === 'attack') return true;
  if (typeof entry.attackBonus === 'number') return true;
  
  // Explicit weapon attack in description
  if (/(melee|ranged)\s*weapon\s*attack/i.test(desc)) return true;
  
  // Check for weapon-like damage pattern
  if (entry.damage && /\d+d\d+/i.test(String(entry.damage))) {
    // And weapon keywords in name or description
    const text = `${name} ${desc}`.toLowerCase();
    if (/(melee|ranged)\s*attack|weapon|hit|reach|range(?!\s*spell)/i.test(text)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Get the appropriate badge type for an entry
 * date_of_change: 2026-05-03
 * rule_reference: Badge assignment priority: Spell > Attack > Bonus/Reaction > Feature
 */
const getEntryBadgeType = (entry) => {
  // Priority 1: Spells (blue badge)
  if (isSpellEntry(entry)) {
    return 'spell';
  }
  
  // Priority 2: Attacks (red badge)
  if (isAttackEntry(entry)) {
    return 'attack';
  }
  
  // Priority 3: Activation type based
  const activationType = normalizeDisplayText(
    entry?.activationType || entry?.actionType || 'action'
  ).toLowerCase();
  
  if (activationType.includes('bonus')) return 'bonus';
  if (activationType.includes('reaction')) return 'reaction';
  if (activationType.includes('legendary')) return 'legendary';
  if (activationType.includes('free') || activationType.includes('object')) return 'free';
  
  // Default to action
  return 'action';
};

const getKnownCombatResourceKey = (entry) => {
  const explicit = String(entry?.mechanics?.resourceKey || '').trim();
  if (explicit) return explicit;

  const name = normalizeDisplayText(entry?.name || entry?.title || entry?.label).toLowerCase();
  if (name === 'lay on hands') return 'layOnHands';
  if (name === 'rage') return 'rage';
  return '';
};

const resolveLiveCombatResource = (subject, entry) => {
  const resourceKey = getKnownCombatResourceKey(entry);
  if (!resourceKey) return null;

  const branches = [
    subject,
    subject?.data,
    subject?.schemaData,
    subject?.canonicalData,
    subject?.characterData,
    subject?.runtime,
    subject?.visualState
  ].filter(Boolean);

  return branches
    .map((branch) => branch?.combatResources?.[resourceKey])
    .find((resource) => resource && typeof resource === 'object') || null;
};

const enrichEntryWithLiveResource = (subject, entry) => {
  const liveResource = resolveLiveCombatResource(subject, entry);
  if (!liveResource) return entry;

  return {
    ...entry,
    uses: {
      ...(entry?.uses || {}),
      ...(Number.isFinite(Number(liveResource.max)) ? { max: Number(liveResource.max) } : {}),
      ...(Number.isFinite(Number(liveResource.current)) ? { current: Number(liveResource.current) } : {})
    }
  };
};

const buildEntryMeta = (entry) => {
  // date_of_change: 2026-05-03
  // rule_reference: Use smart badge detection instead of just entry.type
  const badgeType = getEntryBadgeType(entry);
  
  const resetLabel = entry?.uses?.resetOn
    ? ({ day: 'daily', '7 days': 'after 7 days', 'long rest': 'after a Long Rest', 'short rest': 'after a Short Rest' }[String(entry.uses.resetOn).toLowerCase()] || `resets ${entry.uses.resetOn}`)
    : '';
  const usesMax = Number(entry?.uses?.max);
  const usesCurrent = Number(entry?.uses?.current);
  const hasUsesMax = Number.isFinite(usesMax) && usesMax > 0;
  const hasUsesCurrent = Number.isFinite(usesCurrent) && usesCurrent >= 0;
  const isPointPool = Boolean(entry?.mechanics?.resourceSpend)
    || normalizeDisplayText(entry?.name).toLowerCase() === 'lay on hands';
  const resourceSummary = hasUsesMax
    ? (hasUsesCurrent
      ? `${usesCurrent}/${usesMax} ${isPointPool ? 'points' : 'uses'} remaining${resetLabel ? `; ${resetLabel}` : ''}`
      : `${usesMax} ${isPointPool ? 'points' : `use${usesMax === 1 ? '' : 's'}`}${resetLabel ? `; ${resetLabel}` : ''}`)
    : '';
  const metaParts = [
    formatCombatEntryTypeLabel(badgeType),
    formatCombatRangeSummary(entry?.range),
    Number.isFinite(Number(entry?.bonus)) && Number(entry.bonus) !== 0
      ? `${Number(entry.bonus) > 0 ? '+' : ''}${Number(entry.bonus)} to hit`
      : '',
    formatCombatDamageSummary(entry?.damage),
    resourceSummary
  ];
  
  // Add spell level if applicable
  if (badgeType === 'spell' && typeof entry?.level === 'number' && entry.level > 0) {
    metaParts.push(`${entry.level}${['st','nd','rd','th'][entry.level-1] || 'th'} level`);
  }
  
  // Add saving throw DC if applicable
  if (entry?.savingThrow?.dc) {
    metaParts.push(`DC ${entry.savingThrow.dc} ${entry.savingThrow.ability || ''}`);
  }
  
  return metaParts.filter(Boolean);
};

const normalizeEntryForDisplay = (entry, index) => {
  const normalizedEntry = normalizeNamedAction(entry);
  // date_of_change: 2026-05-03
  // rule_reference: Detect proper badge type for consistent UI presentation
  const detectedType = getEntryBadgeType(normalizedEntry);
  
  return {
    key: `${getSectionKeyForEntry(normalizedEntry)}-${normalizeDisplayText(normalizedEntry?.name || normalizedEntry?.title || normalizedEntry?.label || index).toLowerCase()}-${index}`,
    name: normalizeDisplayText(normalizedEntry?.name || normalizedEntry?.title || normalizedEntry?.label || 'Action'),
    description: normalizeDisplayText(normalizedEntry?.desc || normalizedEntry?.description || normalizedEntry?.text || ''),
    meta: buildEntryMeta(normalizedEntry),
    // Use detected type for proper badge rendering, fall back to original
    type: detectedType || normalizedEntry?.type || 'action',
    activationType: normalizedEntry?.activationType || normalizedEntry?.actionType || 'action',
    damageSummary: formatCombatDamageSummary(normalizedEntry?.damage),
    originalEntry: normalizedEntry
  };
};

/**
 * date_of_change: 2026-04-10
 * rule_reference: D&D 5e combat action economy — every sheet surface must present the same derived combat actions the simulator can execute.
 */
export const buildCharacterActionEconomyViewModel = (subject) => {
  const movementSummary = formatMovementSummary(
    subject?.speed
    || subject?.combatStats?.speed
    || subject?.data?.combatStats?.speed
    || subject?.characterData?.speed
    || subject?.characterData?.combatStats?.speed
  );

  if (!subject || typeof subject !== 'object') {
    return {
      movementSummary,
      sections: ACTION_SECTION_DEFINITIONS.map((section) => ({ ...section, entries: [], count: 0 })),
      totalEntryCount: 0,
      hasContent: false
    };
  }

  const bucketedEntries = {
    actions: [],
    bonusActions: [],
    reactions: [],
    specialActions: []
  };

  // date_of_change: 2026-04-10
  // rule_reference: Use the canonical CombatActionCatalog so weapons, spells, and feature-derived actions stay aligned with simulator execution.
  const catalog = buildCombatActionCatalog(subject);
  catalog.forEach((entry, index) => {
    const normalizedEntry = normalizeNamedAction(enrichEntryWithLiveResource(subject, entry));
    const sectionKey = getSectionKeyForEntry(normalizedEntry);
    bucketedEntries[sectionKey].push(normalizeEntryForDisplay(normalizedEntry, index));
  });

  if (hasRecordedFeature(subject, 'Telekinetic') && !catalog.some((entry) => /^telekinetic(?: shove)?$/i.test(normalizeDisplayText(entry?.name)))) {
    const telekinetic = normalizeNamedAction({ name: 'Telekinetic', type: 'bonus', activationType: 'bonus action' });
    bucketedEntries.bonusActions.push(normalizeEntryForDisplay(telekinetic, catalog.length));
  }

  const sections = ACTION_SECTION_DEFINITIONS.map((section) => ({
    ...section,
    entries: bucketedEntries[section.key],
    count: bucketedEntries[section.key].length
  }));

  const totalEntryCount = sections.reduce((sum, section) => sum + section.count, 0);

  return {
    movementSummary,
    sections,
    totalEntryCount,
    hasContent: totalEntryCount > 0
  };
};

export default buildCharacterActionEconomyViewModel;
